import { useCallback, useState } from 'react';
import { API } from '@/lib/api';
import { log } from '@/lib/log';
import { toast } from 'sonner';

/**
 * useMessageStream - encapsulates the SSE chat streaming flow.
 *
 * Exposes a `send` function that:
 *  - Issues POST /api/chat/stream with the cookie-based session
 *  - Streams chunks via ReadableStream
 *  - Surfaces start/chunk/done/error events via callbacks
 *  - Returns a 402 path when the user/anon cap is reached
 *
 * Extracted from Chat.jsx to drop that component below the complexity
 * threshold and make the streaming logic independently testable.
 */
export function useMessageStream({
  onStart,
  onChunk,
  onDone,
  onCap,
  onError,
}) {
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState('');

  const send = useCallback(async ({ agentId, message, conversationId, trySessionId }) => {
    if (!agentId || !message?.trim()) return;
    setSending(true);
    setStreamingText('');

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: agentId,
          message,
          conversation_id: conversationId || null,
          try_session_id: trySessionId || null,
        }),
      });

      if (resp.status === 402) {
        const data = await resp.json().catch(() => ({}));
        if (onCap) onCap(data?.detail || 'Free limit reached');
        return { capped: true };
      }
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      let convId = conversationId || null;
      let anonRemaining = null;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const raw = line.replace(/^data:\s*/, '');
          let evt;
          try { evt = JSON.parse(raw); } catch { continue; }
          if (evt.type === 'start') {
            convId = evt.conversation_id;
            if (onStart) onStart(evt.conversation_id);
          } else if (evt.type === 'chunk') {
            acc += evt.content || '';
            setStreamingText(acc);
            if (onChunk) onChunk(acc, evt.content);
          } else if (evt.type === 'done') {
            anonRemaining = typeof evt.anon_remaining === 'number' ? evt.anon_remaining : null;
          } else if (evt.type === 'error') {
            toast.error(evt.detail || 'LLM error');
          }
        }
      }
      setStreamingText('');
      if (onDone) onDone({ conversationId: convId, anonRemaining });
      return { capped: false, conversationId: convId, anonRemaining };
    } catch (err) {
      log.warn('stream failed', err);
      toast.error(err?.message || 'Streaming failed');
      if (onError) onError(err);
      return { capped: false, error: err };
    } finally {
      setSending(false);
    }
  }, [onStart, onChunk, onDone, onCap, onError]);

  return { send, sending, streamingText };
}
